import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Building2, BadgeCheck, AlertTriangle, Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { providerApi } from "@/lib/api/provider";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";
import { fileToCompressedDataUri } from "@/lib/imageUpload";

const TYPES = ["CLINIC", "HOSPITAL", "PHARMACY", "LAB", "RADIOLOGY_CENTER", "OTHER"];

export default function ProfilePage() {
    const { t } = useI18n();
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;
        providerApi.me()
            .then((d) => { if (active) { setData(d); setForm(d); } })
            .catch((err) => active && toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message));
        return () => { active = false; };
    }, []);

    const logoInputRef = useRef(null);
    const [logoUploading, setLogoUploading] = useState(false);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const handleLogoFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error(t("provider.profile.logoInvalidType"));
            return;
        }
        setLogoUploading(true);
        try {
            const uri = await fileToCompressedDataUri(file, { maxDim: 512, quality: 0.9 });
            update({ logo_url: uri });
        } catch {
            toast.error(t("provider.profile.logoError"));
        } finally {
            setLogoUploading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                description_en: form.description_en || null,
                description_ar: form.description_ar || null,
                logo_url: form.logo_url || null,
                type: form.type,
                license_number: form.license_number || null,
                license_expiry: form.license_expiry || null,
            };
            const fresh = await providerApi.update(payload);
            setData(fresh); setForm(fresh);
            toast.success(t("provider.profile.saved"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-provider-profile-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const expiryDays = data.license_days_to_expiry;
    const expiryWarning = expiryDays !== null && expiryDays !== undefined && expiryDays <= 30;

    return (
        <div className="max-w-3xl space-y-6 fade-up" data-testid="page-provider-profile">
            <header>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("provider.profile.title")}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t("provider.profile.subtitle")}</p>
            </header>

            <div className="surface-card p-6">
                <div className="flex items-start gap-4">
                    <div className="icon-tile h-14 w-14 shrink-0">
                        <Building2 className="h-7 w-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-semibold">{data.corporate_name}</h2>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{data.kyc_status}</Badge>
                            {data.kyc_status === "APPROVED" && (
                                <Badge className="bg-success text-white text-[10px]">
                                    <BadgeCheck className="h-3 w-3 me-1" />
                                    {t("provider.profile.approved")}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>
                {expiryWarning && (
                    <div
                        className="mt-5 flex items-start gap-2 rounded-xl bg-destructive/5 border border-destructive/30 px-3 py-2 text-sm text-destructive"
                        data-testid="license-expiry-warning"
                    >
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{t("provider.profile.licenseExpiringSoon", { days: expiryDays })}</span>
                    </div>
                )}
            </div>

            <form onSubmit={save} className="surface-card p-6 space-y-5" data-testid="profile-form">
                <div>
                    <Label>{t("provider.profile.fields.type")}</Label>
                    <Select value={form.type || "CLINIC"} onValueChange={(v) => update({ type: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="profile-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {TYPES.map((tp) => (
                                <SelectItem key={tp} value={tp}>{t(`provider.types.${tp}`)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="desc_en">{t("provider.profile.fields.descriptionEn")}</Label>
                        <Textarea
                            id="desc_en"
                            rows={3}
                            value={form.description_en || ""}
                            onChange={(e) => update({ description_en: e.target.value })}
                            className="mt-1.5"
                            data-testid="profile-description-en"
                        />
                    </div>
                    <div>
                        <Label htmlFor="desc_ar">{t("provider.profile.fields.descriptionAr")}</Label>
                        <Textarea
                            id="desc_ar"
                            rows={3}
                            dir="rtl"
                            lang="ar"
                            value={form.description_ar || ""}
                            onChange={(e) => update({ description_ar: e.target.value })}
                            className="mt-1.5 font-arabic"
                            data-testid="profile-description-ar"
                        />
                    </div>
                </div>

                {/* Logo upload */}
                <div>
                    <Label>{t("provider.profile.fields.logo")}</Label>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                        {t("provider.profile.logoHint")}
                    </p>
                    <div className="flex items-center gap-4">
                        {/* Preview */}
                        <div
                            className="h-20 w-20 rounded-2xl border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:border-primary/50 transition-colors"
                            onClick={() => logoInputRef.current?.click()}
                            data-testid="logo-preview"
                        >
                            {logoUploading ? (
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : form.logo_url ? (
                                <img
                                    src={form.logo_url}
                                    alt="Logo preview"
                                    className="h-full w-full object-contain"
                                />
                            ) : (
                                <ImageIcon className="h-7 w-7 text-muted-foreground/50" />
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => logoInputRef.current?.click()}
                                disabled={logoUploading}
                                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
                                data-testid="logo-upload-btn"
                            >
                                <Upload className="h-4 w-4" />
                                {form.logo_url ? t("provider.profile.logoChange") : t("provider.profile.logoUpload")}
                            </button>
                            {form.logo_url && (
                                <button
                                    type="button"
                                    onClick={() => update({ logo_url: null })}
                                    className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors"
                                    data-testid="logo-remove-btn"
                                >
                                    <X className="h-4 w-4" />
                                    {t("provider.profile.logoRemove")}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Hidden file input */}
                    <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                        className="hidden"
                        onChange={(e) => handleLogoFile(e.target.files?.[0])}
                        data-testid="logo-file-input"
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="lic">{t("provider.profile.fields.licenseNumber")}</Label>
                        <Input
                            id="lic"
                            value={form.license_number || ""}
                            onChange={(e) => update({ license_number: e.target.value })}
                            className="mt-1.5"
                            data-testid="profile-license-number"
                        />
                    </div>
                    <div>
                        <Label htmlFor="lic_exp">{t("provider.profile.fields.licenseExpiry")}</Label>
                        <Input
                            id="lic_exp"
                            type="date"
                            value={form.license_expiry || ""}
                            onChange={(e) => update({ license_expiry: e.target.value })}
                            className="mt-1.5"
                            data-testid="profile-license-expiry"
                        />
                    </div>
                </div>

                <div className="flex justify-end">
                    <Button type="submit" disabled={saving} data-testid="profile-save">
                        {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
                        {t("common.save")}
                    </Button>
                </div>
            </form>
        </div>
    );
}
